import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card } from '../ui/Card';
import { EmptyState, LoadingSkeleton } from '../ui/States';
import { formatCurrency } from '../../utils/formatters';

const DONUT_COLORS = [
  '#0f766e',
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0891b2',
  '#4f46e5',
  '#be123c',
  '#9333ea',
  '#0369a1',
  '#15803d',
  '#c2410c',
  '#475569',
  '#0e7490',
];

const getCustomerTotal = (item, type) => {
  const rawValue =
    type === 'invoice'
      ? item.totalPurchase
      : type === 'payment'
      ? item.totalPayment
      : item.totalReturn;
  const value = Number(rawValue);

  return Number.isFinite(value) ? value : 0;
};

const getCustomerName = (item, fallback) =>
  `${item.firstName || ''} ${item.lastName || ''}`.trim() || fallback;

export const buildDonutData = (customers, type, fallback = 'Customer') => ({
  labels: customers.map((customer) => getCustomerName(customer, fallback)),
  values: customers.map((customer) => getCustomerTotal(customer, type)),
});

const getPrefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const ColumnChartComponent = ({
  type,
  customers = [],
  loading = false,
  error = '',
  showFinancialValues = false,
}) => {
  const { t, i18n } = useTranslation();
  const isDarkMode = useSelector((state) => state.darkMode.isDarkMode);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);
  const chartAreaRef = useRef(null);

  const chartTitle =
    type === 'invoice'
      ? t('Total Purchases')
      : type === 'payment'
      ? t('Total Payments')
      : t('Total Returns');

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) return undefined;

    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', updatePreference);

    return () => mediaQuery.removeEventListener?.('change', updatePreference);
  }, []);

  const chartData = useMemo(
    () =>
      customers
        .filter((customer) => getCustomerTotal(customer, type) > 0)
        .sort((a, b) => getCustomerTotal(b, type) - getCustomerTotal(a, type)),
    [customers, type],
  );

  const donutData = useMemo(
    () => buildDonutData(chartData, type, t('Customer')),
    [chartData, t, type],
  );
  const total = donutData.values.reduce((sum, value) => sum + value, 0);
  const chartKey = `${type}-${isDarkMode ? 'dark' : 'light'}-${chartData.length}-${total}-${
    showFinancialValues ? 'visible' : 'hidden'
  }`;

  const clearHoveredSegment = useCallback(() => {
    setHoveredSegment((current) => (current ? null : current));
  }, []);

  useEffect(() => {
    setSelectedCustomer(null);
    clearHoveredSegment();
  }, [chartData, clearHoveredSegment]);

  const handleSegmentHover = useCallback((event, config) => {
    const chartArea = chartAreaRef.current;
    const slice = event?.target?.closest?.('.apexcharts-pie-area') || event?.target;
    if (!chartArea || !slice?.getBoundingClientRect) return;

    const chartRect = chartArea.getBoundingClientRect();
    const sliceRect = slice.getBoundingClientRect();
    const pointX = sliceRect.left + sliceRect.width / 2 - chartRect.left;
    const pointY = sliceRect.top + sliceRect.height / 2 - chartRect.top;
    const isRightSide = pointX >= chartRect.width / 2;
    const calloutWidth = 176;
    const labelLeft = isRightSide
      ? Math.min(pointX + 38, chartRect.width - calloutWidth - 8)
      : Math.max(pointX - calloutWidth - 38, 8);

    setHoveredSegment({
      index: config.dataPointIndex,
      pointX,
      pointY,
      labelLeft,
      labelTop: Math.max(10, Math.min(pointY - 46, 310)),
      isRightSide,
      width: chartRect.width,
      height: Math.min(chartRect.height, 380),
    });
  }, []);

  const handleChartMouseMove = useCallback(
    (event) => {
      if (!event.target.closest?.('.apexcharts-pie-area')) clearHoveredSegment();
    },
    [clearHoveredSegment],
  );

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'donut',
      height: 380,
      width: '100%',
      fontFamily: '"Segoe UI", Arial, sans-serif',
      parentHeightOffset: 0,
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      toolbar: { show: false },
      animations: {
        enabled: !prefersReducedMotion,
        easing: 'easeinout',
        speed: 760,
        animateGradually: {
          enabled: true,
          delay: 75,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 380,
        },
      },
      events: {
        dataPointSelection: (_, __, config) => {
          const customer = chartData[config.dataPointIndex];
          if (customer) setSelectedCustomer(customer);
        },
        dataPointMouseEnter: (event, __, config) => handleSegmentHover(event, config),
        dataPointMouseLeave: clearHoveredSegment,
      },
    },
    labels: donutData.labels,
    colors: DONUT_COLORS,
    stroke: {
      width: 4,
      colors: [isDarkMode ? '#111827' : '#ffffff'],
    },
    dataLabels: { enabled: false },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 500,
      labels: {
        colors: isDarkMode ? '#cbd5e1' : '#475569',
      },
      markers: {
        width: 9,
        height: 9,
        radius: 2,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
      formatter: (seriesName) =>
        seriesName.length > 18 ? `${seriesName.slice(0, 18)}...` : seriesName,
    },
    plotOptions: {
      pie: {
        expandOnClick: true,
        donut: {
          size: '66%',
          labels: {
            show: showFinancialValues,
            name: {
              show: true,
              offsetY: 16,
              color: isDarkMode ? '#94a3b8' : '#64748b',
            },
            value: {
              show: true,
              offsetY: -16,
              color: isDarkMode ? '#f8fafc' : '#0f172a',
              fontSize: '16px',
              fontWeight: 700,
              formatter: (value) => formatCurrency(Number(value), i18n.language),
            },
            total: {
              show: true,
              showAlways: true,
              label: chartTitle,
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '12px',
              fontWeight: 500,
              formatter: () => formatCurrency(total, i18n.language),
            },
          },
        },
      },
    },
    tooltip: {
      enabled: false,
    },
    states: {
      hover: { filter: { type: 'lighten', value: 0.18 } },
      active: { filter: { type: 'darken', value: 0.12 } },
    },
    responsive: [
      {
        breakpoint: 520,
        options: {
          chart: { height: 330 },
          legend: { fontSize: '11px' },
        },
      },
    ],
  }), [
    chartData,
    chartTitle,
    clearHoveredSegment,
    donutData.labels,
    handleSegmentHover,
    i18n.language,
    isDarkMode,
    prefersReducedMotion,
    showFinancialValues,
    total,
  ]);

  return (
    <Card className='min-w-0 p-5'>
      <div className='mb-2'>
        <h2 className='text-base font-bold text-[var(--text-primary)]'>{chartTitle}</h2>
        <p className='mt-1 text-sm text-[var(--text-secondary)]'>{t('Grouped by customer')}</p>
      </div>
      {loading ? (
        <LoadingSkeleton rows={5} />
      ) : error ? (
        <EmptyState title={error} description={t('Try refreshing the page.')} />
      ) : chartData.length === 0 ? (
        <EmptyState title={t('No data available')} description={t('There are no values to chart yet.')} />
      ) : (
        <div
          ref={chartAreaRef}
          className='dashboard-donut-chart relative min-h-[380px] min-w-0'
          onMouseMove={handleChartMouseMove}
          onMouseLeave={clearHoveredSegment}
        >
          <Chart
            key={chartKey}
            options={chartOptions}
            series={donutData.values}
            type='donut'
            width='100%'
            height={380}
          />
          {hoveredSegment && chartData[hoveredSegment.index] && (
            <div className='pointer-events-none absolute inset-x-0 top-0 z-10 h-[380px]'>
              <svg
                className='absolute inset-0 h-full w-full overflow-visible'
                viewBox={`0 0 ${hoveredSegment.width} ${hoveredSegment.height}`}
                preserveAspectRatio='none'
                aria-hidden='true'
              >
                <path
                  d={`M ${hoveredSegment.pointX} ${hoveredSegment.pointY} L ${
                    hoveredSegment.isRightSide
                      ? hoveredSegment.pointX + 24
                      : hoveredSegment.pointX - 24
                  } ${hoveredSegment.pointY} L ${
                    hoveredSegment.isRightSide
                      ? hoveredSegment.pointX + 24
                      : hoveredSegment.pointX - 24
                  } ${hoveredSegment.labelTop + 26} L ${
                    hoveredSegment.isRightSide
                      ? hoveredSegment.labelLeft - 8
                      : hoveredSegment.labelLeft + 184
                  } ${hoveredSegment.labelTop + 26}`}
                  fill='none'
                  stroke={isDarkMode ? '#94a3b8' : '#64748b'}
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div
                className='dashboard-donut-callout absolute w-44 rounded-md border border-[var(--border-strong)] bg-[var(--surface-panel)] px-3 py-2 shadow-[var(--shadow-popover)]'
                style={{ left: hoveredSegment.labelLeft, top: hoveredSegment.labelTop }}
              >
                <p className='truncate text-xs font-semibold text-[var(--text-primary)]'>
                  {getCustomerName(chartData[hoveredSegment.index], t('Customer'))}
                </p>
                <p className='mt-1 text-sm font-bold text-[var(--text-secondary)]'>
                  {formatCurrency(getCustomerTotal(chartData[hoveredSegment.index], type), i18n.language)}
                </p>
              </div>
            </div>
          )}
          {selectedCustomer && (
            <div className='mt-2 flex items-center justify-between border-t border-[var(--border-subtle)] pt-3 text-sm'>
              <span className='font-semibold text-[var(--text-primary)]'>
                {getCustomerName(selectedCustomer, t('Customer'))}
              </span>
              <span className='font-semibold text-[var(--text-secondary)]'>
                {formatCurrency(getCustomerTotal(selectedCustomer, type), i18n.language)}
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default ColumnChartComponent;
